import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefundWindowLog } from "@/components/admin/RefundWindowLog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import api, { formatApiError } from "@/lib/api";
import { DataTable } from "./_shared";
import { X, RotateCcw, ShieldCheck, ShieldAlert, GraduationCap, DollarSign, Send, Mail, MessageCircle, Zap, ChevronDown } from "lucide-react";

const TESTIDS = {
  root: "admin-users-root",
  search: "admin-users-search",
  tierFilter: "admin-users-tier-filter",
  statusFilter: "admin-users-status-filter",
  row: "admin-users-row",
  drawer: "admin-users-drawer",
  refundBtn: "admin-users-refund-button",
  tierSelectBtn: "admin-users-tier-change-button",
  nudgeBtn: "admin-users-nudge-button",
  nudgeChannelBtn: "admin-users-nudge-channel-button",
  bulkNudgeBtn: "admin-users-bulk-nudge-button",
};

export default function AdminUsers() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [status, setStatus] = useState("all");
  const [activity, setActivity] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (tier !== "all") p.set("tier", tier);
    if (status !== "all") p.set("status", status);
    if (activity !== "all") p.set("activity", activity);
    api.get(`/admin/users?${p.toString()}`).then(({ data }) => setRows(data)).catch(() => {});
  }, [q, tier, status, activity]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (row) => {
    setSelected(row);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/admin/users/${row.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load user");
      setSelected(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const columns = useMemo(() => [
    { key: "email", label: t("admin.users.cols.email") },
    { key: "name", label: t("admin.users.cols.name") },
    { key: "tier", label: t("admin.users.cols.tier"), render: (r) => (
        <Badge className={r.tier === "premium" ? "bg-blue-800" : r.tier === "basic" ? "bg-slate-700" : "bg-slate-300 text-slate-700"}>{r.tier}</Badge>
      ) },
    { key: "subscription_status", label: t("admin.users.cols.status"), render: (r) => (
        r.suspended
          ? <Badge className="bg-amber-500 text-white">Suspended</Badge>
          : <span className="text-slate-600 text-sm capitalize">{r.subscription_status}</span>
      ) },
    { key: "lessons_completed", label: t("admin.users.cols.lessons"), render: (r) => (
        <span className="tabular-nums">{r.lessons_completed}</span>
      ) },
    { key: "days_idle", label: "Idle", render: (r) => {
        if (r.days_idle == null) {
          return <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Never started</span>;
        }
        const tone =
          r.days_idle >= 30 ? "text-rose-700 bg-rose-50 border-rose-200"    :
          r.days_idle >= 14 ? "text-amber-700 bg-amber-50 border-amber-200" :
          r.days_idle >= 7  ? "text-slate-700 bg-slate-100 border-slate-200":
                              "text-emerald-700 bg-emerald-50 border-emerald-200";
        return (
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-mono tabular-nums font-semibold ${tone}`}>
            {r.days_idle}d
          </span>
        );
      }
    },
    { key: "first_premium_access_at", label: t("admin.users.cols.firstAccess"),
      render: (r) => r.first_premium_access_at
        ? <span className="text-xs text-slate-500">{new Date(r.first_premium_access_at).toLocaleDateString()}</span>
        : <span className="text-xs text-emerald-600 font-semibold">— never —</span>
    },
    { key: "created_at", label: t("admin.users.cols.joined"),
      render: (r) => <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span> },
  ], [t]);

  return (
    <div data-testid={TESTIDS.root} className="space-y-6">
      <div>
        <h1 className="font-display font-black text-4xl text-slate-900 tracking-tight">{t("admin.users.title")}</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">{t("admin.users.subtitle")}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          data-testid={TESTIDS.search}
          placeholder={t("admin.users.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-11 max-w-md"
        />
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger data-testid={TESTIDS.tierFilter} className="h-11 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.tierAll")}</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={TESTIDS.statusFilter} className="h-11 w-48">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
            <SelectItem value="never_activated">Never activated (paid, no access)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Activity chip strip — surface the retention cohorts the automated
          nudge system already targets, so admins can hand-pick who deserves
          a personal DM on top of the email. */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mr-1">
          Activity
        </span>
        {[
          { id: "all",           label: "All",              tone: "slate"   },
          { id: "active_7d",     label: "Active · 7d",      tone: "emerald" },
          { id: "stalled_14d",   label: "Stalled · 14d+",   tone: "amber"   },
          { id: "stalled_30d",   label: "Stalled · 30d+",   tone: "rose"    },
          { id: "never_started", label: "Never started",    tone: "slate"   },
        ].map((c) => {
          const on = activity === c.id;
          const base = "px-3 py-1.5 rounded-full border text-[11px] font-bold uppercase tracking-widest transition-colors";
          const tones = {
            slate:   on ? "bg-slate-900 text-white border-slate-900"       : "bg-white text-slate-600 border-slate-200 hover:border-slate-400",
            emerald: on ? "bg-emerald-600 text-white border-emerald-600"    : "bg-white text-emerald-700 border-emerald-200 hover:border-emerald-400",
            amber:   on ? "bg-amber-500 text-slate-950 border-amber-500"    : "bg-white text-amber-700 border-amber-200 hover:border-amber-400",
            rose:    on ? "bg-rose-600 text-white border-rose-600"          : "bg-white text-rose-700 border-rose-200 hover:border-rose-400",
          };
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`admin-users-activity-${c.id}`}
              aria-pressed={on}
              onClick={() => setActivity(c.id)}
              className={`${base} ${tones[c.tone]}`}
            >
              {c.label}
            </button>
          );
        })}
        {activity !== "all" && (
          <span className="ml-auto text-xs text-slate-500 tabular-nums">
            {rows.length} match{rows.length === 1 ? "" : "es"}
          </span>
        )}
        {activity !== "all" && rows.length > 0 && (
          <Button
            data-testid={TESTIDS.bulkNudgeBtn}
            onClick={() => setBulkOpen(true)}
            className="h-8 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold uppercase tracking-widest text-[11px]"
          >
            <Send size={12} className="mr-1.5" />
            Bulk nudge {rows.length}
          </Button>
        )}
      </div>

      <BulkNudgeDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        userIds={rows.map((r) => r.id)}
        activityLabel={activity}
      />

      <DataTable
        empty={t("admin.empty")}
        columns={columns}
        rows={rows}
        rowTestId={TESTIDS.row}
        onRowClick={openDetail}
      />

      <UserDrawer
        open={!!selected}
        loading={loadingDetail}
        detail={detail}
        row={selected}
        onClose={() => { setSelected(null); setDetail(null); }}
        onRefunded={load}
      />
    </div>
  );
}

function UserDrawer({ open, loading, detail, row, onClose, onRefunded }) {
  const { t } = useTranslation();
  const [changing, setChanging] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [nudging, setNudging] = useState(null); // `${course_id}:${channel}` being nudged
  const [suspending, setSuspending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!open) return null;

  const nudge = async (courseId, channel = "email") => {
    setNudging(`${courseId}:${channel}`);
    try {
      const { data } = await api.post(`/admin/users/${row.id}/nudge`, {
        course_id: courseId,
        channel,
      });
      const ch = data.channels || {};
      const okChannels = Object.entries(ch).filter(([, v]) => v?.ok).map(([k]) => k);
      const failed = Object.entries(ch).filter(([, v]) => !v?.ok);
      if (okChannels.length) {
        toast.success(
          `Nudge sent via ${okChannels.join(" + ")} at ${data.milestone}% milestone`,
        );
      }
      failed.forEach(([k, v]) => {
        const reason = v?.reason || "failed";
        const readable =
          reason === "not_linked" ? "user hasn't linked Discord" :
          reason === "rate_limited" ? "already nudged in the last 24h" :
          reason;
        toast.error(`${k}: ${readable}`);
      });
      if (!okChannels.length && !failed.length) {
        toast.error(t("admin.users.nudgeError"));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("admin.users.nudgeError"));
    } finally {
      setNudging(null);
    }
  };

  const refund = async (sessionId) => {
    if (!window.confirm(t("admin.users.refundConfirm", { session: sessionId.slice(0, 16) }))) return;
    setRefunding(true);
    try {
      await api.post(`/admin/users/${row.id}/refund`, { session_id: sessionId, reason: "admin_override" });
      toast.success(t("admin.users.refundSuccess"));
      onRefunded?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("admin.users.refundError"));
    } finally {
      setRefunding(false);
    }
  };

  const changeTier = async (newTier) => {
    setChanging(true);
    try {
      const { data } = await api.post(`/admin/users/${row.id}/tier`, { tier: newTier });
      if (data.was_upgrade && data.email_sent) {
        toast.success(`Tier changed to ${newTier} — welcome email sent`);
      } else if (data.was_upgrade && !data.email_sent) {
        toast.success(`Tier changed to ${newTier} — email skipped (send failed)`);
      } else {
        toast.success(t("admin.users.tierChanged"));
      }
      onRefunded?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setChanging(false);
    }
  };

  const suspend = async () => {
    const reason = window.prompt(
      "Reason for suspension (optional — shown in the audit log, not to the user):",
      "",
    );
    if (reason === null) return; // user hit Cancel
    if (!window.confirm(`Suspend ${row.email}? They will be logged out and blocked from signing back in until reactivated.`)) return;
    setSuspending(true);
    try {
      await api.post(`/admin/users/${row.id}/suspend`, { reason });
      toast.success("Account suspended");
      onRefunded?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSuspending(false);
    }
  };

  const reactivate = async () => {
    setSuspending(true);
    try {
      await api.post(`/admin/users/${row.id}/reactivate`);
      toast.success("Account reactivated");
      onRefunded?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSuspending(false);
    }
  };

  const demote = async () => {
    if (!window.confirm(`Remove admin role from ${row.email}? They'll keep the account but lose access to /admin. Reversible from Users → set role.`)) return;
    setSuspending(true);
    try {
      await api.post(`/admin/users/${row.id}/demote`);
      toast.success("Admin role removed — user is now a member");
      onRefunded?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSuspending(false);
    }
  };

  const remove = async () => {
    const typed = window.prompt(
      `PERMANENT deletion of ${row.email}.\n\nThis wipes their courses progress, journal entries, quizzes, certificates, and signed agreements. Payment records are kept for tax/audit but PII is scrubbed.\n\nType DELETE to confirm:`,
    );
    if (typed !== "DELETE") {
      if (typed !== null) toast.info("Deletion cancelled — you must type DELETE exactly.");
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${row.id}`);
      toast.success("Account deleted");
      onRefunded?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={TESTIDS.drawer} className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {row?.name || row?.email}
          </DialogTitle>
          <div className="text-sm text-slate-500">{row?.email}</div>
        </DialogHeader>

        {loading && <div className="py-10 text-center text-slate-500">{t("common.loading")}</div>}

        {detail && (
          <div className="space-y-6">
            {/* Snapshot */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniCard label={t("admin.users.cols.tier")} value={detail.user.tier} />
              <MiniCard label={t("admin.users.cols.status")} value={detail.user.subscription_status} />
              <MiniCard
                label={t("admin.users.contentAccessed")}
                value={detail.user.first_premium_access_at ? t("common.yes") : t("common.no")}
                accent={!!detail.user.first_premium_access_at}
              />
              <MiniCard label={t("admin.users.cols.lessons")} value={row?.lessons_completed ?? 0} />
            </div>

            {/* Tier override */}
            <Section title={t("admin.users.tierOverride")} icon={<ShieldCheck size={16} />}>
              <div className="flex flex-wrap items-center gap-2">
                {["free", "basic", "premium"].map((t2) => (
                  <Button
                    key={t2}
                    data-testid={`${TESTIDS.tierSelectBtn}-${t2}`}
                    variant={detail.user.tier === t2 ? "default" : "outline"}
                    disabled={changing || detail.user.tier === t2}
                    onClick={() => changeTier(t2)}
                    className={detail.user.tier === t2 ? "bg-blue-800 text-white" : "border-slate-300"}
                  >
                    {t2}
                  </Button>
                ))}
              </div>
            </Section>

            {/* Danger zone — suspend / reactivate / delete */}
            <Section title="Danger zone" icon={<ShieldAlert size={16} />}>
              {detail.user.suspended ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
                    <div className="font-semibold text-amber-900">Account is currently suspended</div>
                    {detail.user.suspended_at && (
                      <div className="text-xs text-amber-800 mt-1">
                        Since {new Date(detail.user.suspended_at).toLocaleString()}
                        {detail.user.suspended_reason ? ` · Reason: ${detail.user.suspended_reason}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      data-testid="admin-users-reactivate-button"
                      onClick={reactivate}
                      disabled={suspending}
                      className="bg-emerald-700 hover:bg-emerald-600 text-white"
                    >
                      {suspending ? "Working…" : "Reactivate account"}
                    </Button>
                    <Button
                      data-testid="admin-users-delete-button"
                      onClick={remove}
                      disabled={deleting}
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      {deleting ? "Deleting…" : "Delete permanently"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong>Suspend</strong> blocks sign-in and logs the user out immediately, but keeps all their data. Reversible with one click.<br />
                    <strong>Delete</strong> is permanent — wipes progress, journal, quizzes, certificates and agreements. Payment records are kept but scrubbed of PII.
                    {detail.user.role === "admin" && (
                      <><br /><strong>Demote</strong> removes admin privileges — required before you can suspend or delete another admin.</>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {detail.user.role === "admin" && (
                      <Button
                        data-testid="admin-users-demote-button"
                        onClick={demote}
                        disabled={suspending}
                        variant="outline"
                        className="border-blue-300 text-blue-800 hover:bg-blue-50"
                      >
                        {suspending ? "Working…" : "Demote to member"}
                      </Button>
                    )}
                    <Button
                      data-testid="admin-users-suspend-button"
                      onClick={suspend}
                      disabled={suspending}
                      variant="outline"
                      className="border-amber-300 text-amber-800 hover:bg-amber-50"
                    >
                      {suspending ? "Working…" : "Suspend account"}
                    </Button>
                    <Button
                      data-testid="admin-users-delete-button"
                      onClick={remove}
                      disabled={deleting}
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      {deleting ? "Deleting…" : "Delete permanently"}
                    </Button>
                  </div>
                </div>
              )}
            </Section>

            {/* Course progress */}
            <Section title={t("admin.users.courseProgress")} icon={<GraduationCap size={16} />}>
              {detail.course_progress.length === 0 ? (
                <p className="text-sm text-slate-500">{t("admin.users.noProgress")}</p>
              ) : (
                <div className="space-y-2">
                  {detail.course_progress.map((c) => (
                    <div key={c.course_id} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="font-semibold text-slate-800 text-sm">{c.title}</div>
                        <div className="text-xs text-slate-500">
                          {c.completed}/{c.total} {t("admin.users.lessons")}
                        </div>
                      </div>
                      <div className="w-32 bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div className="bg-blue-800 h-full" style={{ width: `${c.percent}%` }} />
                      </div>
                      <div className="text-sm font-semibold tabular-nums text-slate-800 w-10 text-right">{c.percent}%</div>
                      {c.percent > 0 && c.percent < 100 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              data-testid={`${TESTIDS.nudgeBtn}-${c.course_id.slice(-6)}`}
                              variant="outline"
                              disabled={nudging?.startsWith(c.course_id)}
                              className="border-amber-300 text-amber-800 hover:bg-amber-50 h-8 px-2.5"
                              title={t("admin.users.nudgeTooltip")}
                            >
                              <Send size={12} className="mr-1" />
                              {t("admin.users.nudge")}
                              <ChevronDown size={12} className="ml-1 opacity-60" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">
                              Channel
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                              data-testid={`${TESTIDS.nudgeChannelBtn}-email-${c.course_id.slice(-6)}`}
                              onClick={() => nudge(c.course_id, "email")}
                            >
                              <Mail size={14} className="mr-2" /> Email only
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`${TESTIDS.nudgeChannelBtn}-discord-${c.course_id.slice(-6)}`}
                              onClick={() => nudge(c.course_id, "discord")}
                            >
                              <MessageCircle size={14} className="mr-2" /> Discord DM
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              data-testid={`${TESTIDS.nudgeChannelBtn}-both-${c.course_id.slice(-6)}`}
                              onClick={() => nudge(c.course_id, "both")}
                              className="font-semibold"
                            >
                              <Zap size={14} className="mr-2 text-amber-600" /> Both (Email + Discord)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Quizzes */}
            <Section title={t("admin.users.quizzes")}>
              {detail.quiz_summary.length === 0 ? (
                <p className="text-sm text-slate-500">{t("admin.users.noQuizzes")}</p>
              ) : (
                <ul className="text-sm space-y-1.5">
                  {detail.quiz_summary.map((qz) => (
                    <li key={qz.quiz_id} className="flex justify-between">
                      <span className="text-slate-700">{qz.title}</span>
                      <span className={`font-mono tabular-nums ${qz.passed ? "text-emerald-600 font-semibold" : "text-slate-500"}`}>
                        {qz.best_score}% {qz.passed ? "✓" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Refund window evidence */}
            <Section title="Refund window" icon={<ShieldAlert size={16} />}>
              <RefundWindowLog rw={detail.refund_window} user={detail.user} />
            </Section>

            {/* Payments + refund */}
            <Section title={t("admin.users.payments")} icon={<DollarSign size={16} />}>
              {detail.payments.length === 0 ? (
                <p className="text-sm text-slate-500">{t("admin.users.noPayments")}</p>
              ) : (
                <ul className="space-y-2">
                  {detail.payments.map((p) => (
                    <li key={p.session_id} className="border border-slate-200 rounded-lg p-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <Badge className="bg-slate-700">{p.tier}</Badge>
                          <span className="font-semibold text-slate-800">${p.amount.toFixed(2)}</span>
                          <span className="text-xs text-slate-500">{p.currency?.toUpperCase()}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 font-mono truncate">{p.session_id}</div>
                        <div className="text-xs text-slate-400">
                          {p.created_at ? new Date(p.created_at).toLocaleString() : ""}
                          {p.refunded && <span className="ml-2 text-amber-700 font-semibold">refunded · {p.refund_channel}</span>}
                        </div>
                      </div>
                      {!p.refunded && p.processed && (
                        <Button
                          data-testid={`${TESTIDS.refundBtn}-${p.session_id.slice(-6)}`}
                          variant="outline"
                          disabled={refunding}
                          onClick={() => refund(p.session_id)}
                          className="border-amber-300 text-amber-800 hover:bg-amber-50"
                        >
                          <RotateCcw size={14} className="mr-1" /> {t("admin.users.refund")}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Agreements */}
            <Section title={t("admin.users.agreements")} icon={<ShieldAlert size={16} />}>
              {detail.agreements.length === 0 ? (
                <p className="text-sm text-slate-500">{t("admin.users.noAgreements")}</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {detail.agreements.map((a) => (
                    <li key={a.id} className="flex justify-between text-slate-700">
                      <span className="font-display italic">{a.full_name}</span>
                      <span className="text-xs text-slate-500 font-mono">
                        {a.tier} · {a.agreement_version} · {new Date(a.signed_at).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Certificates */}
            <Section title={t("admin.users.certificates")}>
              {detail.certificates.length === 0 ? (
                <p className="text-sm text-slate-500">{t("admin.users.noCertificates")}</p>
              ) : (
                <ul className="text-sm space-y-1">
                  {detail.certificates.map((c) => (
                    <li key={c.id} className="text-slate-700">
                      <span className="font-mono text-xs text-amber-700">{c.code}</span> · {c.course_title}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MiniCard({ label, value, accent }) {
  return (
    <div className={`rounded-lg px-3 py-2 border ${accent ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`mt-0.5 font-semibold text-sm ${accent ? "text-amber-900" : "text-slate-800"}`}>{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
        {icon} {title}
      </div>
      <div>{children}</div>
    </section>
  );
}

function BulkNudgeDialog({ open, onClose, userIds, activityLabel }) {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [channel, setChannel] = useState("email");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/content/courses")
      .then(({ data }) => {
        setCourses(data);
        // Pre-select the first course so admin isn't blocked by an empty form.
        if (data.length && !courseId) setCourseId(data[0].id || data[0]._id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    if (!courseId) {
      toast.error("Pick a course first");
      return;
    }
    if (!window.confirm(
      `Send ${channel === "both" ? "Email + Discord DM" : channel} nudge to ${userIds.length} user${userIds.length === 1 ? "" : "s"}?\n\nRate-limited to once per 24h per user per channel.`,
    )) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/users/nudge/bulk", {
        course_id: courseId,
        channel,
        user_ids: userIds,
      });
      const s = data.summary || {};
      const emailSent = s.sent?.email ?? 0;
      const discordSent = s.sent?.discord ?? 0;
      toast.success(
        `Sent ${emailSent} email · ${discordSent} Discord · ${s.skipped_rate_limited || 0} rate-limited · ${s.skipped_not_linked || 0} not linked · ${s.failed || 0} failed`,
      );
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk nudge failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-users-bulk-nudge-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Bulk nudge {userIds.length} user{userIds.length === 1 ? "" : "s"}
          </DialogTitle>
          <div className="text-xs text-slate-500">
            Cohort: <span className="font-mono">{activityLabel}</span> · Rate-limited per user per channel to once every 24h.
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Course</div>
            <Select value={courseId} onValueChange={setCourseId}>
              <SelectTrigger data-testid="admin-users-bulk-nudge-course" className="h-11">
                <SelectValue placeholder="Pick the course to reference…" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {courses.map((c) => (
                  <SelectItem key={c.id || c._id} value={c.id || c._id}>
                    <span className="inline-flex items-center gap-2">
                      <Badge className="bg-slate-200 text-slate-700 text-[10px] uppercase">{c.language}</Badge>
                      {c.title}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
              Nudge references this course's completion milestone. Users with 0% progress on it are skipped.
            </p>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Channel</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "email",   icon: <Mail size={14} />,          label: "Email" },
                { id: "discord", icon: <MessageCircle size={14} />, label: "Discord DM" },
                { id: "both",    icon: <Zap size={14} />,           label: "Both" },
              ].map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`admin-users-bulk-nudge-channel-${c.id}`}
                  onClick={() => setChannel(c.id)}
                  aria-pressed={channel === c.id}
                  className={`h-11 rounded-lg border text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${
                    channel === c.id
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {c.icon}
                  {c.label}
                </button>
              ))}
            </div>
            {channel !== "email" && (
              <p className="mt-2 text-[11px] text-amber-700 leading-relaxed">
                Users who haven't linked Discord will be silently skipped (counted in the summary).
              </p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
            <Button
              data-testid="admin-users-bulk-nudge-send"
              onClick={send}
              disabled={sending || !courseId}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"
            >
              {sending ? "Sending…" : `Send to ${userIds.length}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
